import { motion } from 'framer-motion';
import { ShoppingCart, Heart, Star, Plus, Minus, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '@/context/CartContext';
import { useWishlist } from '@/context/WishlistContext';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { useState, useRef, useCallback, useMemo } from 'react';

const SWIPE_THRESHOLD = 35; // px horizontal distance to count as a swipe

const ProductCard = ({ product }) => {
  const navigate = useNavigate();
  const { addToCart, getItemQuantity, updateQuantity } = useCart();
  const { isInWishlist, toggleWishlist } = useWishlist();
  const { isAuthenticated } = useAuth();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const imageContainerRef = useRef(null);
  const touchState = useRef({ startX: 0, startY: 0, active: false, moved: false, swiped: false });

  const quantityInCart = getItemQuantity(product.id);

  const allImages = useMemo(() => {
    const images = [product.image_url];
    if (product.images && product.images.length > 0) {
      product.images.forEach(img => {
        if (img !== product.image_url && !images.includes(img)) {
          images.push(img);
        }
      });
    }
    return images.slice(0, 5);
  }, [product.image_url, product.images]);

  const hasMultipleImages = allImages.length > 1;

  const handleAddToCart = (e) => {
    e.stopPropagation();
    if (product.stock <= 0) {
      toast.error('This product is out of stock');
      return;
    }
    addToCart(product);
    toast.success(`Added to cart`);
  };

  const handleIncrement = (e) => {
    e.stopPropagation();
    if (quantityInCart >= product.stock) {
      toast.error(`Only ${product.stock} items available`);
      return;
    }
    addToCart(product);
  };

  const handleDecrement = (e) => {
    e.stopPropagation();
    updateQuantity(product.id, quantityInCart - 1);
  };

  const handleWishlistToggle = (e) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      toast.error('Please login to add to wishlist');
      return;
    }
    toggleWishlist(product.id);
  };

  const handleMouseMove = useCallback((e) => {
    if (!imageContainerRef.current || !hasMultipleImages) return;
    const rect = imageContainerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const imageIndex = Math.min(
      Math.floor(percentage * allImages.length),
      allImages.length - 1
    );
    setCurrentImageIndex(imageIndex);
  }, [hasMultipleImages, allImages.length]);

  const handleMouseLeave = useCallback(() => {
    setCurrentImageIndex(0);
  }, []);

  // Touch swipe on mobile — swipe left/right to cycle images
  const handleTouchStart = useCallback((e) => {
    if (!hasMultipleImages) return;
    const t = e.touches[0];
    touchState.current = { startX: t.clientX, startY: t.clientY, active: true, moved: false, swiped: false };
  }, [hasMultipleImages]);

  const handleTouchMove = useCallback((e) => {
    const st = touchState.current;
    if (!st.active || !hasMultipleImages) return;
    const t = e.touches[0];
    const dx = t.clientX - st.startX;
    const dy = t.clientY - st.startY;
    if (Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) {
      // Horizontal intent — prevent vertical scroll from stealing the gesture
      st.moved = true;
      if (e.cancelable) e.preventDefault();
    }
  }, [hasMultipleImages]);

  const handleTouchEnd = useCallback((e) => {
    const st = touchState.current;
    if (!st.active || !hasMultipleImages) {
      touchState.current.active = false;
      return;
    }
    const t = e.changedTouches[0];
    const dx = t.clientX - st.startX;
    if (Math.abs(dx) >= SWIPE_THRESHOLD) {
      st.swiped = true;
      setCurrentImageIndex((prev) => {
        if (dx < 0) return Math.min(prev + 1, allImages.length - 1);
        return Math.max(prev - 1, 0);
      });
    }
    touchState.current.active = false;
  }, [hasMultipleImages, allImages.length]);

  const handleCardClick = useCallback(() => {
    // If the user just swiped, don't navigate — let them keep browsing images
    if (touchState.current.swiped) {
      touchState.current.swiped = false;
      return;
    }
    navigate(`/product/${product.id}`);
  }, [navigate, product.id]);

  const isOutOfStock = product.stock <= 0;
  const isPreOrder = !!product.is_pre_order;
  const discountPercent = product.original_price && product.original_price > product.price
    ? Math.round(((product.original_price - product.price) / product.original_price) * 100)
    : 0;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      whileHover={{ y: -4 }}
      transition={{ type: 'spring', stiffness: 400, damping: 25 }}
      className="group cursor-pointer h-full"
      onClick={handleCardClick}
      onMouseLeave={handleMouseLeave}
      data-testid={`product-card-${product.id}`}
    >
      <div className={`bg-white rounded-lg overflow-hidden border border-slate-100 hover:border-slate-200 hover:shadow-md transition-all duration-300 h-full flex flex-col ${isOutOfStock ? 'opacity-70' : ''}`}>
        {/* Image Container - Papita.ae style */}
        <div 
          ref={imageContainerRef}
          className="relative aspect-square overflow-hidden bg-slate-50 touch-pan-y select-none"
          onMouseMove={handleMouseMove}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          data-testid={`product-image-container-${product.id}`}
        >
          {!imageLoaded && (
            <div className="absolute inset-0 bg-slate-100 animate-pulse" />
          )}
          
          <img
            src={allImages[currentImageIndex]}
            alt={product.name}
            className={`w-full h-full object-cover transition-transform duration-300 group-hover:scale-105 ${isOutOfStock ? 'grayscale' : ''}`}
            onLoad={() => setImageLoaded(true)}
            onError={(e) => {
              // Graceful fallback if image fails to load
              if (!e.target.dataset.fallbackApplied) {
                e.target.dataset.fallbackApplied = '1';
                e.target.src = "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 400'><rect width='400' height='400' fill='%23f1f5f9'/><g fill='%2394a3b8'><circle cx='200' cy='170' r='40'/><path d='M120 280 L160 220 L200 250 L240 200 L280 280 Z'/></g><text x='200' y='340' font-family='system-ui' font-size='18' fill='%2364748b' text-anchor='middle'>Image coming soon</text></svg>";
                setImageLoaded(true);
              }
            }}
            loading="lazy"
            decoding="async"
          />
          
          {/* Image indicator dots */}
          {hasMultipleImages && (
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1 z-10">
              {allImages.map((_, idx) => (
                <div
                  key={`dot-${product.id}-${idx}`}
                  className={`w-1.5 h-1.5 rounded-full transition-all duration-200 ${
                    idx === currentImageIndex ? 'bg-slate-800' : 'bg-slate-400'
                  }`}
                />
              ))}
            </div>
          )}

          {/* Discount Badge / Pre-Order Badge - Top Left */}
          {isPreOrder ? (
            <div className="absolute top-2 left-2 bg-purple-600 text-white text-[10px] font-bold px-2 py-0.5 rounded z-10 flex items-center gap-1" data-testid={`pre-order-badge-${product.id}`}>
              <Clock className="w-3 h-3" /> PRE-ORDER
            </div>
          ) : discountPercent > 0 ? (
            <div className="absolute top-2 left-2 bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded z-10">
              -{discountPercent}%
            </div>
          ) : null}
          
          {/* Low Stock Badge */}
          {!isPreOrder && product.stock > 0 && product.stock <= 5 && !discountPercent && (
            <div className="absolute top-2 left-2 bg-orange-500 text-white text-[10px] font-semibold px-2 py-0.5 rounded z-10">
              Only {product.stock} left
            </div>
          )}
          
          {/* Wishlist button - Top Right */}
          <button
            onClick={handleWishlistToggle}
            onMouseMove={(e) => e.stopPropagation()}
            className="absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center bg-white shadow-sm hover:shadow-md transition-all z-10"
            data-testid={`wishlist-btn-${product.id}`}
          >
            <Heart 
              className={`w-4 h-4 transition-colors ${
                isInWishlist(product.id) 
                  ? 'text-red-500 fill-red-500' 
                  : 'text-slate-400 hover:text-red-400'
              }`} 
            />
          </button>

          {/* Add to Cart - Bottom Right (hidden for pre-order products) */}
          {!isPreOrder && (
            <div className="absolute bottom-2 right-2 z-10" onMouseMove={(e) => e.stopPropagation()}>
            {quantityInCart > 0 ? (
              <div 
                className="flex items-center bg-[#0047AB] rounded-lg shadow-lg overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={handleDecrement}
                  className="w-8 h-8 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
                  data-testid={`decrement-${product.id}`}
                >
                  <Minus className="w-3.5 h-3.5" />
                </button>
                <span className="w-6 h-8 flex items-center justify-center text-white font-bold text-sm">
                  {quantityInCart}
                </span>
                <button
                  onClick={handleIncrement}
                  disabled={quantityInCart >= product.stock}
                  className="w-8 h-8 flex items-center justify-center text-white hover:bg-white/20 transition-colors disabled:opacity-50"
                  data-testid={`increment-${product.id}`}
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <button
                onClick={handleAddToCart}
                disabled={isOutOfStock}
                className={`w-9 h-9 rounded-lg flex items-center justify-center shadow-md transition-all ${
                  isOutOfStock 
                    ? 'bg-slate-200 cursor-not-allowed' 
                    : 'bg-[#0047AB] hover:bg-[#003380]'
                }`}
                data-testid={`add-to-cart-${product.id}`}
              >
                <Plus className={`w-5 h-5 ${isOutOfStock ? 'text-slate-400' : 'text-white'}`} />
              </button>
            )}
          </div>
          )}
        </div>

        {/* Product Info - Papita.ae clean style */}
        <div className="p-3 space-y-1 flex flex-col flex-1">
          {/* Brand */}
          {product.brand && (
            <p className="text-[10px] text-slate-400 uppercase tracking-wide font-medium truncate">
              {product.brand}
            </p>
          )}

          {/* Product Name */}
          <h3 className="font-medium text-slate-900 line-clamp-2 text-sm leading-5 min-h-[2.5rem]">
            {product.is_grouped && product.group_name ? product.group_name : product.name}
          </h3>

          {/* Variant count badge for grouped products */}
          {product.is_grouped && product.variant_count > 1 && (
            <span className="inline-flex items-center text-[10px] font-medium text-[#0047AB] bg-blue-50 px-2 py-0.5 rounded-full" data-testid={`variant-count-${product.id}`}>
              {product.variant_count} variants available
            </span>
          )}

          {/* Rating */}
          {product.avg_rating > 0 && (
            <div className="flex items-center gap-1">
              <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
              <span className="text-xs font-medium text-slate-600">
                {product.avg_rating.toFixed(1)}
              </span>
              <span className="text-xs text-slate-400">
                ({product.rating_count})
              </span>
            </div>
          )}

          {/* Price Section - pinned to bottom for consistent alignment */}
          <div className="mt-auto pt-1">
            {isPreOrder ? (
              <>
                <div className="flex items-center gap-1.5 text-purple-700 font-semibold text-sm">
                  <Clock className="w-3.5 h-3.5" />
                  Pre-Order
                </div>
                <p className="text-[10px] text-slate-400 min-h-[15px]">Tap to reserve — no payment yet</p>
              </>
            ) : (
              <>
                <div className="flex items-baseline gap-1.5 flex-wrap">
                  <span className="text-base font-bold text-slate-900">
                    ₹{product.price.toLocaleString()}
                  </span>
                  {product.original_price && product.original_price > product.price && (
                    <span className="text-xs text-slate-400 line-through">
                      ₹{product.original_price.toLocaleString()}
                    </span>
                  )}
                </div>
                <p className="text-[10px] text-emerald-600 font-medium min-h-[15px]">
                  {product.shipping_fee === 0 ? 'Free Shipping' : '\u00A0'}
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ProductCard;
